// MFA Code Submission Modal
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import axios from 'axios';
import API from '../config';

export default function MFAModal({ open, onClose, onSuccess, token }) {
  const [mfaCode, setMfaCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mfaCode || mfaCode.length !== 5) {
      setError('Please enter a valid 5-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/api/subscriptions/submit-mfa-code`,
        { mfaCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Success - call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      setMfaCode('');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit MFA code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMfaCode('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          m: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <SecurityIcon 
            sx={{ 
              fontSize: 32, 
              color: theme.palette.primary.main 
            }} 
          />
        </Box>
        
        <Typography 
          variant="h5" 
          fontWeight={700} 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Two-Factor Authentication
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, px: { xs: 1, sm: 0 } }}
        >
          Enter the 5-digit code from your authenticator app or OnlyFans email
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 4 }, pt: 2, pb: 1 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            OnlyFans requires a verification code. Check your email or authenticator app.
          </Alert>

          <TextField
            autoFocus
            fullWidth
            label="Verification Code"
            value={mfaCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ''); // Only digits
              if (value.length <= 5) {
                setMfaCode(value);
              }
            }}
            placeholder="00000"
            variant="outlined"
            inputProps={{
              maxLength: 5,
              pattern: '[0-9]*',
              inputMode: 'numeric',
              style: {
                fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                textAlign: 'center',
                letterSpacing: window.innerWidth < 600 ? '0.3rem' : '0.5rem',
                fontWeight: 600,
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          <Typography 
            variant="caption" 
            color="text.secondary" 
            display="block" 
            sx={{ mt: 1, textAlign: 'center' }}
          >
            Enter the 5-digit code from SMS or your authenticator app
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: { xs: 2, sm: 4 }, 
          pb: { xs: 3, sm: 4 }, 
          pt: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ borderRadius: 2, order: { xs: 2, sm: 1 } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || mfaCode.length !== 5}
          fullWidth={isMobile}
          sx={{
            px: 4,
            borderRadius: 2,
            order: { xs: 1, sm: 2 },
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Verifying...
            </>
          ) : (
            'Submit Code'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

